import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, CloudDownload, CheckCircle, FileText, Paperclip, File as FileIcon, Eye } from "lucide-react";
import { toast } from "sonner";
import { getAppMondayFiles, uploadStandingOrderFile, uploadStandingOrderFromMonday, previewMondayFile } from "@/lib/api";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

const fmtSize = (b) => (!b ? "" : b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)}MB` : `${Math.ceil(b / 1024)}KB`);

// "Upload standing order" — attach the SO (raw file or pulled straight from
// the deal's Monday files). Required BEFORE the loan can be added to LoanDisk;
// queued files are pushed onto the loan automatically at creation.
export const StandingOrderUpload = ({ application, onUploaded }) => {
  const [mondayOpen, setMondayOpen] = useState(false);
  const [files, setFiles] = useState(null);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [uploading, setUploading] = useState(null); // "raw" | "batch" | null
  const [selected, setSelected] = useState(new Set());
  const inputRef = useRef(null);
  const appId = application.id;
  const hasLoan = !!application.loandisk_loan_id;

  const pickMonday = async () => {
    setMondayOpen(true);
    setLoadingFiles(true);
    setSelected(new Set());
    try {
      const r = await getAppMondayFiles(appId);
      setFiles(r.files || []);
    } catch (e) {
      toast.error(errorDetailMessage(e, "Couldn't load Monday files"));
      setFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  };

  const toggle = (id) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const sendSelected = async () => {
    const chosen = (files || []).filter((f) => selected.has(f.id));
    if (!chosen.length) return;
    setUploading("batch");
    let ok = 0, fail = 0, queued = false;
    for (const f of chosen) {
      try {
        const r = await uploadStandingOrderFromMonday(appId, { asset_id: f.id, name: f.name, url: f.url });
        ok += 1;
        if (r?.queued) queued = true;
      } catch (e) {
        fail += 1;
        toast.error(`${f.name}: ${errorDetailMessage(e, "attach failed")}`);
      }
    }
    if (ok) {
      toast.success(queued
        ? `${ok} file(s) attached — pushed onto the LoanDisk loan automatically when it's created`
        : `${ok} file(s) uploaded to LoanDisk`);
    }
    if (!fail) setMondayOpen(false);
    setSelected(new Set());
    setUploading(null);
    onUploaded?.();
  };

  const sendRaw = async (file) => {
    setUploading("raw");
    try {
      const r = await uploadStandingOrderFile(appId, file);
      toast.success(r?.queued ? r.message : `Standing order "${file.name}" uploaded to LoanDisk`);
      onUploaded?.();
    } catch (e) {
      toast.error(errorDetailMessage(e, "Standing order attach failed"));
    } finally {
      setUploading(null);
    }
  };

  return (
    <div className="mt-4 p-4 border rounded-lg bg-slate-50/60 space-y-3" data-testid="standing-order-upload-card">
      <div>
        <p className="text-sm font-medium flex items-center gap-1.5">
          <FileText className="w-4 h-4 text-slate-500" /> Standing Order document
        </p>
        <p className="text-xs text-muted-foreground">
          Attach the SO — pulled from the deal&apos;s Monday files or uploaded raw. It goes onto the LoanDisk
          loan together with the signed contracts.
        </p>
      </div>

      {application.standing_order_file_uploaded && (
        <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2" data-testid="so-uploaded-status">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          <span className="truncate">{application.standing_order_file_name}</span>
          {application.standing_order_file_pending_loandisk && (
            <span className="text-[10px] font-medium text-amber-700 bg-amber-100 border border-amber-200 rounded px-1.5 py-0.5 whitespace-nowrap" data-testid="so-pending-loandisk-chip">
              pushes to LoanDisk when the loan is created
            </span>
          )}
          <span className="text-xs text-emerald-600 ml-auto whitespace-nowrap">
            {application.standing_order_file_uploaded_at && new Date(application.standing_order_file_uploaded_at).toLocaleString("en-GB")}
          </span>
        </div>
      )}

      {!hasLoan && (
        <p className="text-xs text-amber-700" data-testid="so-needs-loan-hint">
          Required before the loan can be added to LoanDisk — attach it now and it&apos;s pushed onto the
          loan automatically at the moment the loan is created.
        </p>
      )}

      <div className="flex gap-2">
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,image/*"
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) sendRaw(f); e.target.value = ""; }}
          data-testid="so-file-input"
        />
        <Button variant="outline" size="sm" disabled={!!uploading} onClick={() => inputRef.current?.click()} data-testid="so-attach-btn">
          {uploading === "raw" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Paperclip className="w-4 h-4 mr-1.5" />}
          Upload standing order
        </Button>
        <Button variant="outline" size="sm" disabled={!!uploading} onClick={pickMonday} data-testid="so-from-monday-btn">
          <CloudDownload className="w-4 h-4 mr-1.5" />
          From Monday files
        </Button>
      </div>

      <Dialog open={mondayOpen} onOpenChange={(v) => { if (!uploading) setMondayOpen(v); }}>
        <DialogContent className="max-w-lg" data-testid="so-monday-files-dialog">
          <DialogHeader>
            <DialogTitle>Attach files from Monday</DialogTitle>
            <DialogDescription>
              Tick the file(s) to attach — use the eye to preview before attaching.
            </DialogDescription>
          </DialogHeader>
          {loadingFiles ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading Monday files…
            </div>
          ) : !files?.length ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No files found on the Monday item.</p>
          ) : (
            <>
              <div className="space-y-1.5 max-h-[50vh] overflow-y-auto">
                {files.map((f) => (
                  <div
                    key={f.id}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm transition-colors ${
                      selected.has(f.id) ? "border-emerald-400 bg-emerald-50/60" : "bg-white hover:bg-slate-50"
                    }`}
                    data-testid={`so-monday-file-${f.id}`}
                  >
                    <input type="checkbox" checked={selected.has(f.id)} onChange={() => toggle(f.id)}
                      disabled={!!uploading} data-testid={`so-monday-file-check-${f.id}`} />
                    <FileIcon className="w-4 h-4 text-slate-400 shrink-0" />
                    <button type="button" className="truncate flex-1 text-left cursor-pointer" onClick={() => toggle(f.id)} disabled={!!uploading}>
                      {f.name}
                    </button>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{fmtSize(f.size)}</span>
                    {f.url && (
                      <button type="button" title="Preview in a new tab" className="text-slate-400 hover:text-blue-600 shrink-0"
                        onClick={async () => {
                          try { await previewMondayFile(appId, f.id, f.name); }
                          catch (e) { toast.error(errorDetailMessage(e, "Couldn't preview this file")); }
                        }} data-testid={`so-monday-file-preview-${f.id}`}>
                        <Eye className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="ghost" size="sm" disabled={!!uploading} onClick={() => setMondayOpen(false)}>Cancel</Button>
                <Button size="sm" onClick={sendSelected} disabled={!!uploading || selected.size === 0}
                  className="bg-emerald-600 hover:bg-emerald-700" data-testid="so-monday-attach-selected-btn">
                  {uploading === "batch" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Paperclip className="w-4 h-4 mr-1.5" />}
                  Attach {selected.size || ""} file{selected.size === 1 ? "" : "s"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
